import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { isSsoPopupFlow, clearSsoPopupFlow, postSsoResult } from '../../utils/ssoPopup';
import './Login.css';

/**
 * NLNG SSO callback page (route: /sso/callback).
 *
 * The CAS flow redirects the browser here with ?ticket=ST-...&state=... after
 * a successful Azure AD sign-in. Two shapes are supported:
 *
 * - **Popup flow (default).** The login page opened this in a centered popup,
 *   so hand the one-time ticket back to the opener — which owns the auth
 *   context — and close. See `utils/ssoPopup.js`.
 * - **Top-level flow (fallback).** Reached directly in the main tab (popup
 *   blocked, a bookmarked callback, …): exchange the ticket here and forward
 *   the user to their intended route, as this page has always done.
 */
const SsoCallback = () => {
  const { loginWithSso } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [handedOff, setHandedOff] = useState(false);
  // The CAS ticket is single-use, so guard against React 18 StrictMode
  // double-invoking this effect in development.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const ticket = searchParams.get('ticket');
    const state = searchParams.get('state');

    // Popup flow: report back to the login tab and get out of the way.
    if (isSsoPopupFlow()) {
      clearSsoPopupFlow();
      postSsoResult(ticket
        ? { ticket, state: state || '' }
        : { error: 'No SSO ticket was received. Please try signing in again.' });
      setHandedOff(true);
      // Let the message flush before closing; if the browser refuses to close
      // the window, the "you can close this window" panel below stays up.
      const timer = setTimeout(() => {
        try {
          window.close();
        } catch (closeError) {
          /* window stays open with the panel shown */
        }
      }, 150);
      return () => clearTimeout(timer);
    }

    if (!ticket) {
      setError('No SSO ticket was received. Please try signing in again.');
      return;
    }

    const completeSignIn = async () => {
      const result = await loginWithSso(ticket, state || '');
      if (result.success) {
        const intendedRoute = sessionStorage.getItem('intendedRoute') || '/';
        sessionStorage.removeItem('intendedRoute');
        navigate(intendedRoute, { replace: true });
      } else {
        setError(result.error);
      }
    };

    completeSignIn();
  }, [searchParams, loginWithSso, navigate]);

  return (
    <div className="login-simple">
      <div className="login-simple__container">
        <div className="login-simple__brand">
          <div className="login-simple__logo">
            <Shield size={40} strokeWidth={2} />
            <img src="/assets/nlng-logo.png" alt="NLNG Logo" className="sidebar__logo-image" />
          </div>
          <h1 className="login-simple__brand-name">NLNG VR</h1>
          <p className="login-simple__brand-subtitle">VR Training Platform for NLNG Staff</p>
        </div>

        {handedOff ? (
          <div className="sso-callback__pending">
            <CheckCircle size={32} className="sso-callback__done-icon" />
            <p className="sso-callback__pending-text">
              Sign-in received. You can close this window.
            </p>
          </div>
        ) : error ? (
          <div className="sso-callback__error">
            <AlertCircle size={32} />
            <p className="sso-callback__error-text">{error}</p>
            <button
              type="button"
              className="btn btn-primary login-simple__submit"
              onClick={() => navigate('/login', { replace: true })}
            >
              <ArrowLeft size={18} />
              Back to Sign In
            </button>
          </div>
        ) : (
          <div className="sso-callback__pending">
            <span className="sso-callback__spinner" />
            <p className="sso-callback__pending-text">Completing NLNG sign-in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SsoCallback;
